import React, { useState, useEffect } from 'react';
import { Badge } from "@/components/ui/badge";
import { fmtDate, fmtPriceForSymbol } from './DcaUtils';
import { formatPrice, formatQty } from "@/lib/utils";
import { RefreshCw, ClipboardList, TrendingDown } from "lucide-react";

interface CycleDetailViewProps {
  cycleId: number;
  symbol: string;
}

export const CycleDetailView: React.FC<CycleDetailViewProps> = ({ cycleId, symbol }) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const curSym = symbol.endsWith('KRW') ? '₩' : '$';

  useEffect(() => {
    const fetchDetail = async () => {
      setLoading(true);
      try {
        const res = await fetch(`/dca/cycles/${cycleId}/detail`);
        const result = await res.json();
        setData(result);
      } catch (err) {
        console.error("Failed to fetch cycle detail", err);
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [cycleId]);

  if (loading) {
    return (
      <div className="p-8 flex flex-col items-center justify-center gap-2 text-muted-foreground">
        <RefreshCw className="w-5 h-5 animate-spin opacity-20" />
        <span className="text-[10px] font-bold">상세 내역 로딩 중...</span>
      </div>
    );
  }

  if (!data) return <div className="p-8 text-center text-xs text-muted-foreground">데이터를 불러오지 못했습니다.</div>;

  const { cycle, trades, logs } = data;

  return (
    <div className="p-5 bg-muted/20 border-t border-border/50 animate-in fade-in slide-in-from-top-1 duration-200">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Ladder & Trades */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingDown className="w-3.5 h-3.5 text-primary" />
            <span className="text-[10px] font-black uppercase tracking-widest text-foreground">Scaling Targets & Trades</span>
          </div>

          <div className="space-y-2">
            {(cycle.targets || []).map((t: any) => {
              const trade = trades.find((tr: any) => tr.dca_step === t.step_number);
              return (
                <div key={t.id} className={`p-3 rounded-xl border flex justify-between items-center transition-all ${t.status === 'filled' ? 'bg-background border-primary/20 shadow-sm' : 'bg-muted/30 border-border/50 opacity-60'}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black ${t.status === 'filled' ? 'bg-primary text-white' : 'bg-muted-foreground/20 text-muted-foreground'}`}>
                      {t.step_number}
                    </div>
                    <div className="flex flex-col">
                      <span className={`text-[11px] font-bold ${t.status === 'filled' ? 'text-foreground' : 'text-muted-foreground'}`}>
                        {t.status === 'filled' ? 'FILLED' : 'PENDING'}
                      </span>
                      {trade && (
                        <span className="text-[9px] text-muted-foreground font-medium">
                          {fmtDate(trade.created_at).slice(5)} 체결
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-xs font-black ${t.status === 'filled' ? 'text-primary' : 'text-muted-foreground'}`}>
                      {curSym}{fmtPriceForSymbol(t.status === 'filled' ? (trade?.price || t.target_price) : t.target_price, symbol)}
                    </p>
                    <p className="text-[9px] font-bold text-muted-foreground/50">
                      Qty: {formatQty(t.target_qty, 4)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right: Specific Logs for this Cycle */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <ClipboardList className="w-3.5 h-3.5 text-primary" />
            <span className="text-[10px] font-black uppercase tracking-widest text-foreground">Cycle Timeline (reversed)</span>
          </div>

          <div className="bg-background/50 rounded-2xl border border-border/50 overflow-hidden divide-y divide-border/30">
            {logs.length > 0 ? (
              [...logs].reverse().map((log: any) => (
                <div key={log.id} className="p-3 flex justify-between gap-4 items-start hover:bg-muted/20 transition-colors">
                  <span className={`text-[11px] leading-relaxed font-medium ${log.message.includes('도달') ? 'text-primary font-bold' : 'text-foreground/80'}`}>
                    {log.message}
                  </span>
                  <span className="text-[9px] text-muted-foreground/40 tabular-nums font-bold whitespace-nowrap mt-0.5">
                    {fmtDate(log.created_at).slice(5)}
                  </span>
                </div>
              ))
            ) : (
              <div className="p-8 text-center text-[10px] text-muted-foreground font-bold opacity-30 italic">관련 로그가 없습니다.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
